import { useCallback, useRef, useState } from "react";
// Polled data is re-fetched on a timer and almost always comes back identical. Handing React a fresh
// copy of identical data makes everything built from it redraw (charts, thumbnails, markers, tables),
// which looks like flicker. These helpers let a poll keep the previous value when nothing changed.

// Structural equality for plain JSON data (what the API returns). Identity first, then a key-by-key
// compare with early exit, so unchanged data is recognised cheaply and changed data stops at the first
// difference.
export function sameValue(a, b) {
  if (a === b) return true;
  if (typeof a !== typeof b || a === null || b === null || typeof a !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) if (!sameValue(a[i], b[i])) return false;
    return true;
  }
  const keysA = Object.keys(a);
  if (keysA.length !== Object.keys(b).length) return false;
  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(b, key) || !sameValue(a[key], b[key])) return false;
  }
  return true;
}

// For a state setter: `setState(keepIfSame(next))` leaves the state (and everything memoised on it)
// untouched when `next` equals what is already there.
export const keepIfSame = (next) => (previous) => (sameValue(previous, next) ? previous : next);

// useState whose setter ignores a value equal to the current one - not even a re-render of the owning
// component (a functional update that returns the old value still costs that render). Accepts a value or
// an updater function, like useState.
export function useStableState(initial) {
  const [value, setValue] = useState(initial);
  const latest = useRef(value);
  const set = useCallback((next) => {
    const resolved = typeof next === "function" ? next(latest.current) : next;
    if (sameValue(latest.current, resolved)) return;
    latest.current = resolved;
    setValue(resolved);
  }, []);
  return [value, set];
}
